import { useMemo } from "react";
import { useDropzone } from "react-dropzone";

import Valkyrie, { viArrowUpToLine, viTriangleExclamation } from "@sippy-platform/valkyrie";

import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";

import { implode } from "../../utils/utility";
import UploadStatus from "./UploadStatus";

export default function Dropzone({ fileTasks, setFileTasks, config }) {
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    ...config,
    maxSize: config?.maxSize ? config.maxSize * 1000000 : undefined,
  });

  const fileTypes = useMemo(() => {
    if (config?.accept) {
      return Object.keys(config.accept)
        .map((mime) => config.accept?.[mime], [])
        .flat()
        .map((extension) => extension?.slice(1));
    }
  }, [config.accept]);

  return (
    <Card
      sx={{
        mt: 1,
        borderRadius: 1,
        textAlign: "center",
        border: (theme) => `2px dashed ${theme.vars.palette.action.disabled}`,
        boxShadow: "none",
        background: "none",
        outline: "none",
        transition: "all .1s ease-in",
        display: "block",
      }}
    >
      {!(config?.multiple === false && fileTasks.length >= 1) && (
        <CardContent
          sx={[
            {
              cursor: "pointer",
              transition: "all .1s ease-in",
              "&:hover": {
                background: (theme) => theme.vars.palette.action.hover,
              },
              display: "block",
            },
            isDragActive && {
              background: (theme) => theme.vars.palette.action.hover,
            },
          ]}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Valkyrie icon={viArrowUpToLine} />
          <Typography>Drop {implode(fileTypes, ", ")} files to upload</Typography>
          <Typography variant="caption">or click to select</Typography>
        </CardContent>
      )}
      {fileTasks.length >= 1 && (
        <Stack>
          {fileTasks.map((task) => (
            <UploadStatus
              file={task.file}
              progress={task.progress}
              controller={task.controller}
              error={task.error}
              setFileTasks={setFileTasks}
              key={task.file?.path}
            />
          ))}
        </Stack>
      )}
      {fileRejections.length >= 1 &&
        fileRejections.map((fileRejection, key) => (
          <CardContent
            sx={{
              px: 1.5,
              py: "8px !important",
              borderTop: (theme) => `1px solid ${theme.vars.palette.divider}`,
            }}
            key={key}
          >
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Valkyrie icon={viTriangleExclamation} />
              <Typography level="body2">
                {fileRejection.errors[0].code === "file-invalid-type"
                  ? `${fileRejection.file.name} is not of type ${fileTypes.join(", ").replace(/, (?=[^,]*$)/, ", or ")}.`
                  : fileRejection.errors[0].code === "file-too-large"
                    ? `${fileRejection.file.name} is larger than ${config.maxSizeMegaByte} MB.`
                    : fileRejection.errors[0].code === "too-many-files"
                      ? `${fileRejection.file.name} couldn't be uploaded because you tried to upload to many files.`
                      : `${fileRejection.file.name}: ${fileRejection.errors[0].code}`}
              </Typography>
            </Box>
          </CardContent>
        ))}
    </Card>
  );
}

Dropzone.propTypes = {
  setFileTasks: PropTypes.func,
  fileTasks: PropTypes.array,
  config: PropTypes.object,
};
